import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TaskReviewModal from "../components/TaskReviewModal";
import Loader from "../components/Loader";
import { fetchMemberProfile, deleteTask } from "../services/apiService";
// Removed unused imports to clear ESLint warnings

export default function MemberProfile({ isDarkMode, toggleDarkMode, userData }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0); 
  
  const [fetchedMember, setFetchedMember] = useState(null);
  const [fetchedTasks, setFetchedTasks] = useState(null);
  const [fetchedStats, setFetchedStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Task Review Modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const isOwnProfile = userData?._id === id;
  const isAdmin = userData?.role === 'admin';

  // --- 1. Memoize default Member data ---
  const defaultMember = useMemo(() => {
    return isOwnProfile && userData ? {
        id: userData._id,
        name: userData.fullname || userData.name || "User",
        email: userData.email,
        rollNumber: userData.roll_no || userData.rollNumber || "N/A",
        section: userData.classes || userData.section || "N/A",
        class: userData.classes || "N/A",
        year: userData.year || "N/A",
        team: userData.team ? userData.team.charAt(0).toUpperCase() + userData.team.slice(1) : "General",
        bio: userData.bio || "Member of IEI Students' Chapter CSE",
        profileImage: userData.avatar || userData.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.fullname || userData.name || 'User'}`,
        joinedDate: userData.createdAt ? new Date(userData.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        phone: userData.phone || "N/A",
        role: userData.role || "user",
        isEmailVerified: userData.isEmailVerified || false
    } : null;
  }, [isOwnProfile, userData]);

  // --- 2. Centralized Data Fetching useEffect ---
  useEffect(() => {
    if (!id) {
        setIsLoading(false);
        return;
    }

    const loadMemberData = async () => {
      setIsLoading(true); 
      const token = localStorage.getItem('accessToken') || ''; 
      
      try {
        if (!token) {
          throw new Error('No access token found');
        }
        
        // Use cached API service
        const result = await fetchMemberProfile(id);
        const apiData = result.data;

        if (apiData && apiData.user) {
          
          // --- A. POPULATE MEMBER PROFILE ---
          const memberData = isOwnProfile ? { ...defaultMember, ...apiData.user } : apiData.user;
          const newMember = {
            id: memberData._id,
            name: memberData.fullname || memberData.name || "User",
            email: memberData.email,
            rollNumber: memberData.roll_no || "N/A",
            section: memberData.classes || "N/A",
            year: memberData.year || "N/A",
            team: memberData.team ? memberData.team.charAt(0).toUpperCase() + memberData.team.slice(1) : "General",
            bio: memberData.bio || "Member of IEI Students' Chapter CSE",
            profileImage: memberData.avatar,
            joinedDate: memberData.createdAt ? new Date(memberData.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            role: memberData.role || "user",
            isEmailVerified: memberData.isEmailVerified || false
          };
          setFetchedMember(newMember);
          
          // --- B. POPULATE TASK HISTORY ---
          const newTasks = (apiData.user.assigned_task || []).map(task => ({
            id: task._id,
            taskName: task.name,
            title: task.name,
            status: task.status,
            priority: task.priority,
            deadline: task.deadline,
          }));
          setFetchedTasks(newTasks);
          
          // --- C. POPULATE MEMBER STATS ---
          const newStats = {
              total: apiData.totalTask || 0,
              completed: apiData.completedTask || 0,
              overdue: apiData.missedTask || 0,
              winRate: (parseFloat(apiData.progressRate) > 0 || apiData.progressRate === 0) ? parseFloat(apiData.progressRate) : 0, 
          };
          setFetchedStats(newStats);

        } else {
            setFetchedMember(null); 
            setFetchedTasks([]);
            setFetchedStats({ total: 0, completed: 0, overdue: 0, winRate: 0 });
        }

      } catch (error) {
        console.error('Error fetching member profile:', error);
        
        // If it's the user's own profile and we have userData, use it as fallback
        if (isOwnProfile && defaultMember) {
          setFetchedMember(defaultMember);
          setFetchedTasks([]);
          setFetchedStats({ total: 0, completed: 0, overdue: 0, winRate: 0 });
        } else {
          setFetchedMember(null);
          setFetchedTasks([]);
          setFetchedStats({ total: 0, completed: 0, overdue: 0, winRate: 0 });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadMemberData();
  }, [id, navigate, isOwnProfile, defaultMember, refreshKey]); 

  // --- External Refresh Listener (Storage only) ---
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'tasks' || e.key === null) {
        setRefreshKey(prev => prev + 1); 
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); 

  // --- API Deletion Logic ---
  const handleDeleteTaskAPI = async (taskId) => {
    const token = localStorage.getItem('accessToken') || ''; 

    try {
        if (!token) {
            throw new Error('No access token found');
        }
        
        // Use API service with automatic cache invalidation
        await deleteTask(taskId);
        
        console.log("✅ Task deleted successfully, cache invalidated");
        return true;

    } catch (error) {
        if (error.message.includes('403')) {
            alert("Error: You do not have permission to delete this task. (Admin access required)");
        } else {
            alert(`Failed to delete task: ${error.message}`);
        }
        return false;
    }
  };

  // --- Task Deletion Handler (Admin Check + API Call) ---
  const handleDeleteTask = async (taskId) => {
    if (!isAdmin) {
        alert("Only admin users can delete tasks.");
        return;
    }

    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
        const success = await handleDeleteTaskAPI(taskId);

        if (success) {
            setRefreshKey(prev => prev + 1);
        }
    }
  };
  
  // --- Task Review Modal Handlers ---
  const handleSubmitReview = (task) => {
    setSelectedTask(task);
    setIsReviewModalOpen(true);
  };
  
  const handleReviewSubmitted = () => {
    // Refresh task list to show updated status from backend
    setRefreshKey(prev => prev + 1);
  };

  const getTeamColor = (team) => {
    const colors = {
      'Management': { bg: '#fff7ed', text: '#ea580c', border: '#fb923c' },
      'Tech': { bg: '#dbeafe', text: '#2563eb', border: '#60a5fa' },
      'Design': { bg: '#fce7f3', text: '#db2777', border: '#f472b6' },
      'PR': { bg: '#f3e8ff', text: '#7c3aed', border: '#a78bfa' }, 
      'Content': { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
      'Media': { bg: '#fee2e2', text: '#dc2626', border: '#f87171' },
      'Support': { bg: '#e0f2fe', text: '#0284c7', border: '#38bdf8' }
    };
    return colors[team] || { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
  };

  // --- LOADING STATE ---
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDarkMode ? '#111827' : '#f8fafc',
        padding: '2rem'
      }}>
        <Loader variant="page" text="Loading member profile..." isDarkMode={isDarkMode} size="lg" />
      </div>
    );
  }

  const member = fetchedMember; 
  const tasks = fetchedTasks || [];
  const stats = fetchedStats || { total: 0, completed: 0, overdue: 0, winRate: 0 }; 

  // --- NOT FOUND STATE ---
  if (!member) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '1rem',
          padding: '2rem',
          color: 'white'
        }}>
          <p style={{ fontSize: '1.25rem', margin: 0 }}>Member not found</p>
        </div>
      </div>
    );
  }

  const teamColor = getTeamColor(member.team);

  const themeBg = isDarkMode 
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : '#f8f9fa';
  
  return (
    <div 
      className={isDarkMode ? '' : 'bg-light'}
      style={{
        minHeight: '100vh',
        padding: 0,
        background: themeBg,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        position: 'relative',
        transition: 'background 0.3s ease',
        width: '100%'
      }}
    >
      <div className="member-profile-container">
        {/* Hero Section */}
        <div 
          className={`${isDarkMode ? '' : 'card shadow-lg'} member-profile-card`}
          style={{
            background: isDarkMode ? 'rgba(20, 20, 30, 0.95)' : '#ffffff',
            borderRadius: '1.25rem',
            padding: '2rem 1.5rem',
            marginBottom: '1.5rem',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
            boxShadow: isDarkMode ? '0 8px 30px rgba(0, 0, 0, 0.35)' : '0 8px 30px rgba(0, 0, 0, 0.05)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 12px 36px rgba(0, 0, 0, 0.45)' : '0 12px 36px rgba(0, 0, 0, 0.08)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 30px rgba(0, 0, 0, 0.35)' : '0 8px 30px rgba(0, 0, 0, 0.05)';
          }}
        >
          {/* Edit Profile Button - Admin Only */}
          {isAdmin && (
          <button
            onClick={() => navigate(`/edit-member/${id}`)}
            className={isDarkMode ? '' : 'btn btn-outline-secondary shadow-sm'}
            style={{
              position: 'absolute',
              top: '1.25rem',
              right: '1.25rem',
              background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #cbd5e1',
              borderRadius: '0.5rem',
              padding: '0.45rem 0.75rem',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 10
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.background = '#6366f1';
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#64748b';
              e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff';
              e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="fas fa-edit"></i>
          </button>
          )}

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.25rem'
          }}>
            <div 
              className="shadow member-profile-picture"
              style={{
                width: '5.5rem',
                height: '5.5rem',
                minWidth: '5.5rem',
                minHeight: '5.5rem',
                maxWidth: '5.5rem',
                maxHeight: '5.5rem',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid #6366f1',
                boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flexShrink: 0
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img
                src={member.profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=6366f1&color=fff`}
                alt={member.name}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=6366f1&color=fff`;
                }}
              />
            </div>

            <div style={{ textAlign: 'center', width: '100%' }}>
              <h1 
                style={{
                  fontSize: '1.65rem',
                  fontWeight: 700,
                  color: isDarkMode ? '#f8fafc' : '#1e293b',
                  marginBottom: '0.35rem',
                  marginTop: 0,
                  letterSpacing: '-0.02em'
                }}
              >
                {member.name}
              </h1>
              <p 
                style={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                  fontSize: '0.95rem',
                  marginBottom: '0.35rem',
                  marginTop: 0,
                  fontWeight: 500
                }}
              >
                <i className="fas fa-id-card" style={{ marginRight: '0.4rem', color: isDarkMode ? '#818cf8' : '#6366f1' }}></i>
                Roll: {member.rollNumber}
              </p>
              <p 
                style={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
                  marginBottom: '0.875rem',
                  marginTop: 0,
                  fontSize: '0.875rem'
                }}
              >
                <i className="fas fa-layer-group" style={{ marginRight: '0.4rem', color: isDarkMode ? '#818cf8' : '#6366f1' }}></i>
                Section {member.section} | {member.year || (member.semester ? `${member.semester}th Semester` : 'N/A')}
              </p>
              {member.bio && (
                <p 
                  style={{
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    marginBottom: '1rem',
                    marginTop: 0,
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    maxWidth: '520px',
                    margin: '0 auto 1rem'
                  }}
                >
                  {member.bio}
                </p>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span 
                  className="badge shadow-sm"
                  style={{
                    padding: '0.5rem 1.25rem',
                    borderRadius: '0.625rem',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${teamColor.bg} 0%, ${teamColor.text}15 100%)`,
                    color: teamColor.text,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: `0 3px 12px ${teamColor.text}25`,
                    border: `1.5px solid ${teamColor.border}`,
                    transition: 'all 0.25s ease'
                  }}
                >
                  <i className={`fas ${
                    member.team === 'Management' ? 'fa-crown' :
                    member.team === 'Tech' ? 'fa-microchip' :
                    member.team === 'Design' ? 'fa-palette' :
                    member.team === 'Content' ? 'fa-file-alt' :
                    member.team === 'PR' ? 'fa-bullhorn' :
                    member.team === 'Media' ? 'fa-video' :
                    member.team === 'Support' ? 'fa-headset' :
                    'fa-users'
                  }`}
                  style={{ fontSize: '0.9rem' }}></i>
                  {member.team} Team
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div 
          className="member-stats-grid"
          style={{
            display: 'grid',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}
        >
          {[
            { label: "Total Tasks", value: stats.total, icon: "fa-calendar", color: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)", bgColor: "#e7f1ff", textColor: "#3b82f6" },
            { label: "Completed", value: stats.completed, icon: "fa-check-circle", color: "linear-gradient(135deg, #10b981 0%, #059669 100%)", bgColor: "#d1fae5", textColor: "#059669" }, 
            { label: "Overdue", value: stats.overdue, icon: "fa-times-circle", color: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", bgColor: "#fee2e2", textColor: "#ef4444" },
            { label: "Progress Rate", value: `${stats.winRate}%`, icon: "fa-chart-line", color: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)", bgColor: "#f3e8ff", textColor: "#8b5cf6" },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className={isDarkMode ? '' : 'card shadow-sm'}
              style={{
                background: isDarkMode ? 'rgba(20, 20, 30, 0.95)' : '#ffffff',
                borderRadius: '1rem',
                padding: '1.25rem 1rem',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                textAlign: 'center',
                transition: 'all 0.25s ease',
                backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
                boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.25)' : '0 4px 16px rgba(0, 0, 0, 0.04)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 24px rgba(0, 0, 0, 0.35)' : '0 8px 24px rgba(0, 0, 0, 0.08)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.25)' : '0 4px 16px rgba(0, 0, 0, 0.04)';
              }}
            >
              <div 
                style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  borderRadius: '50%',
                  background: stat.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.625rem',
                  boxShadow: `0 4px 12px ${stat.textColor}35`
                }}
              >
                <i className={`fas ${stat.icon}`} style={{ color: 'white', fontSize: '1.1rem' }}></i>
              </div>
              <p 
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: stat.textColor,
                  marginBottom: '0.15rem',
                  marginTop: 0,
                  lineHeight: 1.2
                }}
              >
                {stat.value}
              </p>
              <p 
                style={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : '#64748b',
                  fontSize: '0.8125rem',
                  margin: 0,
                  fontWeight: 600
                }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Task History - Responsive Design */}
        <div 
          style={{
            background: isDarkMode ? 'rgba(20, 20, 20, 0.9)' : '#ffffff',
            borderRadius: window.innerWidth <= 375 ? '1rem' : '1.5rem',
            padding: window.innerWidth <= 375 ? '1rem' : window.innerWidth <= 768 ? '1.5rem' : '2rem',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
            boxShadow: isDarkMode 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 4px 20px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            marginTop: window.innerWidth <= 375 ? '1rem' : '1.5rem'
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: window.innerWidth <= 375 ? '0.5rem' : '0.75rem',
            marginBottom: window.innerWidth <= 375 ? '0.75rem' : '1rem'
          }}>
            <div style={{
              width: window.innerWidth <= 375 ? '2rem' : '2.5rem',
              height: window.innerWidth <= 375 ? '2rem' : '2.5rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="fas fa-history" style={{ 
                color: 'white', 
                fontSize: window.innerWidth <= 375 ? '0.875rem' : '1rem' 
              }}></i>
            </div>
            <h2 style={{
              fontSize: window.innerWidth <= 375 ? '1.25rem' : window.innerWidth <= 768 ? '1.5rem' : '1.75rem',
              fontWeight: '700',
              color: isDarkMode ? 'white' : '#1f2937',
              margin: 0
            }}>
              Task History
            </h2>
          </div>

          {/* Task List */}
          {tasks.length > 0 ? (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: window.innerWidth <= 375 ? '0.75rem' : '1rem'
            }}>
              {tasks.map((task, index) => (
                <div
                  key={task.id || index}
                  style={{
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb',
                    borderRadius: window.innerWidth <= 375 ? '0.75rem' : '1rem',
                    padding: window.innerWidth <= 375 ? '0.875rem' : '1rem',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f3f4f6';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  {/* Task Header - Task Name and Delete Button */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start', // Align button to the top
                    marginBottom: '0.75rem',
                    gap: '0.75rem'
                  }}>
                    {/* H3 allows wrapping on unlimited lines to ensure full name visibility */}
                    <h3 style={{
                      fontSize: window.innerWidth <= 375 ? '0.9375rem' : '1rem',
                      fontWeight: '600',
                      color: isDarkMode ? 'white' : '#1f2937',
                      margin: 0,
                      flex: 1,
                      lineHeight: '1.4',
                      // --- NEW: Remove hard line-clamp limit to ensure full text visibility ---
                      overflow: 'hidden', // Required for text-overflow to work if needed
                      textOverflow: 'ellipsis',
                      minWidth: '100px', // Ensures minimal visibility on tiny screens before button
                      // Allow wrapping on unlimited lines:
                      whiteSpace: 'normal', 
                      display: 'block', 
                    }}>
                      {task.taskName || task.title}
                    </h3>

                    {/* Button wrapper ensures button is always visible and does not shrink */}
                    <span style={{ flexShrink: 0, marginLeft: '0.5rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          style={{
                            background: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                            border: `1.5px solid ${isDarkMode ? '#ef4444' : '#f87171'}`,
                            borderRadius: '0.375rem',
                            padding: '0.25rem 0.5rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDarkMode ? 'rgba(239, 68, 68, 0.3)' : '#fca5a5';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fas fa-trash-alt" style={{
                            fontSize: window.innerWidth <= 375 ? '0.75rem' : '0.8125rem',
                            color: isDarkMode ? '#fca5a5' : '#dc2626'
                          }}></i>
                        </button>
                    </span>
                  </div>

                  {/* Status and Priority Badges */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    {/* Status Badge */}
                    {(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const deadline = task.deadline ? new Date(task.deadline) : null;
                      if (deadline) {
                        deadline.setHours(0, 0, 0, 0);
                      }
                                  
                      let statusText = task.status;
                      let statusColor = { bg: '#e0e7ff', text: '#6366f1', icon: 'fa-clock' }; 

                      if (task.status === 'completed') {
                        statusText = 'Completed';
                        statusColor = { bg: '#d1fae5', text: '#059669', icon: 'fa-check-circle' };
                      } else if (task.status === 'missed' || (deadline && deadline < today)) {
                        statusText = 'Missed';
                        statusColor = { bg: '#fee2e2', text: '#dc2626', icon: 'fa-times-circle' };
                      } else if (task.status === 'pending') {
                        statusText = 'Pending';
                        statusColor = { bg: '#fef3c7', text: '#d97706', icon: 'fa-spinner' };
                      } 

                      
                      return (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: window.innerWidth <= 375 ? '0.375rem 0.75rem' : '0.5rem 1rem',
                          borderRadius: '0.5rem',
                          fontSize: window.innerWidth <= 375 ? '0.75rem' : '0.8125rem',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          background: isDarkMode 
                            ? statusColor.bg 
                            : `linear-gradient(135deg, ${statusColor.bg} 0%, ${statusColor.text}20 100%)`,
                          color: statusColor.text,
                          border: `1.5px solid ${statusColor.text}40`,
                          boxShadow: isDarkMode ? 'none' : `0 2px 8px ${statusColor.text}25`
                        }}>
                          <i className={`fas ${statusColor.icon}`} style={{ 
                            fontSize: '0.75rem', 
                            marginRight: '0.5rem' 
                          }}></i>
                          {statusText}
                        </span>
                      );
                    })()}

                    {/* Priority Badge */}
                    {task.priority && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: window.innerWidth <= 375 ? '0.375rem 0.75rem' : '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        fontSize: window.innerWidth <= 375 ? '0.75rem' : '0.8125rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        background: task.priority === 'High' 
                          ? (isDarkMode ? 'rgba(127, 29, 29, 0.6)' : 'linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%)') 
                          : task.priority === 'Medium' 
                          ? (isDarkMode ? 'rgba(120, 53, 15, 0.6)' : 'linear-gradient(135deg, #fef9c3 0%, #fde047 100%)') 
                          : (isDarkMode ? '#374151' : 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'),
                        color: task.priority === 'High'
                          ? (isDarkMode ? '#fca5a5' : '#dc2626')
                          : task.priority === 'Medium'
                          ? (isDarkMode ? '#fde047' : '#ca8a04')
                          : (isDarkMode ? '#d1d5db' : '#6b7280'),
                        border: task.priority === 'High'
                          ? (isDarkMode ? '1.5px solid #fca5a5' : '1.5px solid #f87171')
                          : task.priority === 'Medium'
                          ? (isDarkMode ? '1.5px solid #fde047' : '1.5px solid #facc15')
                          : '1.5px solid #9ca3af',
                        boxShadow: isDarkMode 
                          ? 'none' 
                          : task.priority === 'High'
                          ? '0 2px 8px rgba(252, 165, 165, 0.3)'
                          : task.priority === 'Medium'
                          ? '0 2px 8px rgba(253, 224, 71, 0.3)'
                          : '0 2px 8px rgba(107, 114, 128, 0.25)'
                      }}>
                        <i className="fas fa-flag" style={{ 
                          fontSize: '0.75rem', 
                          marginRight: '0.5rem' 
                        }}></i>
                        {task.priority}
                      </span>
                    )}
                  </div>

                  {/* Task Details - Deadline */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
                    fontSize: window.innerWidth <= 375 ? '0.8125rem' : '0.875rem',
                    marginBottom: isOwnProfile ? '0.75rem' : '0'
                  }}>
                    <i className="fas fa-calendar-alt" style={{ 
                      fontSize: window.innerWidth <= 375 ? '0.75rem' : '0.875rem',
                      opacity: 0.7
                    }}></i>
                    <span>
                      {task.deadline ? new Date(task.deadline).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'No deadline'}
                    </span>
                  </div>
                  
                  {/* Submit Review Button - Always show for own profile, hide completed/missed tasks */}
                  {isOwnProfile && task.status !== 'completed' && task.status !== 'missed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmitReview(task);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem 1rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        fontSize: window.innerWidth <= 375 ? '0.8125rem' : '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <i className="fas fa-check-circle" style={{ fontSize: '0.875rem' }}></i>
                      Submit Review
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: window.innerWidth <= 375 ? '2rem 1rem' : '3rem 2rem',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : '#9ca3af'
            }}>
              <div style={{
                width: window.innerWidth <= 375 ? '3rem' : '4rem',
                height: window.innerWidth <= 375 ? '3rem' : '4rem',
                borderRadius: '50%',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <i className="fas fa-tasks" style={{ 
                  fontSize: window.innerWidth <= 375 ? '1.25rem' : '1.5rem',
                  opacity: 0.5
                }}></i>
              </div>
              <p style={{ 
                margin: 0, 
                fontSize: window.innerWidth <= 375 ? '0.9375rem' : '1rem',
                fontWeight: '500'
              }}>
                No tasks assigned yet
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Task Review Modal */}
      <TaskReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        task={selectedTask}
        memberName={member?.name || 'Unknown Member'}
        memberId={member?.id || userData?._id}
        onSubmit={handleReviewSubmitted}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}